/**
 * iframe → 顶层 Accel Bridge 转发。
 *
 * Native 仅允许主 frame callHandler（非主 frame 会 Rejected: not_main_frame）。
 * Accel 仍可能向子 frame 注入 WebViewJavascriptBridge，故 iframe 内即使看得到
 * 本地 Bridge 也绝不能直调，必须 postMessage 到顶层，由 Host 再 callHandler。
 */

export const BRIDGE_RELAY_REQUEST = "domi-bridge-relay-request";
export const BRIDGE_RELAY_RESULT = "domi-bridge-relay-result";
export const BRIDGE_RELAY_AVAILABLE = "domi-bridge-relay-available";
/** 任意 iframe → 顶层：请求重新广播 Bridge 是否可用 */
export const BRIDGE_RELAY_PING = "domi-bridge-relay-ping";

/** 同源 iframe 可读取；顶层 Bridge 就绪后写入 */
export const BRIDGE_AVAILABLE_STORAGE_KEY = "domi-bridge-available";

const LOG_PREFIX = "[bridge-relay]";

export type BridgeRelayRequestMessage = {
  type: typeof BRIDGE_RELAY_REQUEST;
  requestId: string;
  action: string;
  params: unknown;
  /** 有结果回调的 action 传 `{action}Result`；无回调（如 setLanguage）传 null */
  resultEvent: string | null;
};

export type BridgeRelayResultMessage = {
  type: typeof BRIDGE_RELAY_RESULT;
  requestId: string;
  payload?: unknown;
  error?: string;
};

export type BridgeRelayAvailableMessage = {
  type: typeof BRIDGE_RELAY_AVAILABLE;
  available: boolean;
};

export type BridgeRelayPingMessage = {
  type: typeof BRIDGE_RELAY_PING;
};

type JavascriptBridge = {
  registerHandler: (event: string, cb: (payload: unknown) => void) => void;
  callHandler: (action: string, params: unknown) => void;
};

/** Home 二级 Tab 嵌入标记；部分 WebView 下 parent===window 仍可能误判，靠此强制转发。 */
function hasHomeTabEmbedMarker(): boolean {
  try {
    return new URLSearchParams(window.location.search).get("domiHomeTab") === "1";
  } catch {
    return false;
  }
}

/**
 * 是否嵌在 iframe 内（须转发到顶层再 callHandler）。
 * 多信号：domiHomeTab / top / parent / frameElement。
 * Home 二级 Tab 带 domiHomeTab=1 时一律转发（应对部分 WebView parent===window 误报）。
 * 顶层 TabManager 不会带该参数，故不会误伤主 frame。
 */
export function shouldRelayBridgeToTop(): boolean {
  if (typeof window === "undefined") return false;
  // iframe src 由 withHomeTabEmbedMarker 写入；顶层地址不会带此参数
  if (hasHomeTabEmbedMarker()) return true;
  try {
    if (window.top !== window) return true;
  } catch {
    return true;
  }
  try {
    if (window.parent !== window) return true;
  } catch {
    return true;
  }
  try {
    if (window.frameElement != null) return true;
  } catch {
    return true;
  }
  return false;
}

/** 当前页是否可作为 BridgeRelayHost（真正的主 frame）。 */
export function isBridgeRelayHostFrame(): boolean {
  if (typeof window === "undefined") return false;
  if (hasHomeTabEmbedMarker()) return false;
  try {
    if (window.top !== window) return false;
  } catch {
    return false;
  }
  try {
    if (window.parent !== window) return false;
  } catch {
    return false;
  }
  try {
    if (window.frameElement != null) return false;
  } catch {
    return false;
  }
  return true;
}

/**
 * 转发目标窗口：优先同源 frameElement 所属父窗口，其次 top，再 parent。
 * 避免多层嵌套时只 post 到中间层（中间层 Host 未启用会丢消息）。
 */
export function getBridgeRelayTargetWindow(): Window {
  if (typeof window === "undefined") return window;
  try {
    const frame = window.frameElement as HTMLIFrameElement | null;
    const owner = frame?.ownerDocument?.defaultView;
    if (owner && owner !== window) return owner;
  } catch {
    /* ignore */
  }
  try {
    if (window.top && window.top !== window) return window.top;
  } catch {
    /* cross-origin top */
  }
  try {
    if (window.parent && window.parent !== window) return window.parent;
  } catch {
    /* ignore */
  }
  return window.parent;
}

function isBridgeRelayTargetSource(source: MessageEventSource | null): boolean {
  if (!source) return false;
  try {
    return (
      source === getBridgeRelayTargetWindow() ||
      source === window.parent ||
      source === window.top
    );
  } catch {
    return source === window.parent;
  }
}

/** 二级 iframe 主动问顶层：Bridge 是否已就绪（避免晚加载错过广播）。 */
export function requestTopBridgeAvailability(): void {
  if (typeof window === "undefined" || !shouldRelayBridgeToTop()) return;
  const message: BridgeRelayPingMessage = { type: BRIDGE_RELAY_PING };
  try {
    getBridgeRelayTargetWindow().postMessage(message, window.location.origin);
  } catch {
    /* ignore */
  }
}

/**
 * 等待可用 Bridge：
 * - 顶层：等本地 WebViewJavascriptBridge 注入
 * - 二级 iframe：等顶层标记 / 广播；会先 ping 顶层
 * @returns true 表示可以走 invokeBridgeCall（含向上转发）
 */
export async function waitForBridgeRelayReady(timeoutMs = 8_000): Promise<boolean> {
  if (typeof window === "undefined") return false;

  const hasLocal = () =>
    !!(window as unknown as { WebViewJavascriptBridge?: unknown }).WebViewJavascriptBridge;

  if (!shouldRelayBridgeToTop()) {
    if (hasLocal()) return true;
    return await new Promise<boolean>((resolve) => {
      const timerId = window.setTimeout(() => resolve(hasLocal()), timeoutMs);
      document.addEventListener(
        "miaobi-jsbridge-ready",
        () => {
          window.clearTimeout(timerId);
          resolve(hasLocal());
        },
        { once: true },
      );
    });
  }

  if (isTopBridgeMarkedAvailable()) return true;
  requestTopBridgeAvailability();

  return await new Promise<boolean>((resolve) => {
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timerId);
      window.clearInterval(pollId);
      window.removeEventListener("message", onMessage);
      resolve(ok);
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!isBridgeRelayTargetSource(event.source)) return;
      const data = event.data as BridgeRelayAvailableMessage | null;
      if (!data || data.type !== BRIDGE_RELAY_AVAILABLE) return;
      if (data.available) {
        try {
          sessionStorage.setItem(BRIDGE_AVAILABLE_STORAGE_KEY, "1");
        } catch {
          /* ignore */
        }
        finish(true);
      }
    };

    window.addEventListener("message", onMessage);
    const pollId = window.setInterval(() => {
      if (isTopBridgeMarkedAvailable()) finish(true);
    }, 200);
    const timerId = window.setTimeout(() => finish(isTopBridgeMarkedAvailable()), timeoutMs);
  });
}

export function markTopBridgeAvailable(available: boolean): void {
  if (typeof window === "undefined" || !isBridgeRelayHostFrame()) return;
  try {
    if (available) {
      sessionStorage.setItem(BRIDGE_AVAILABLE_STORAGE_KEY, "1");
    } else {
      sessionStorage.removeItem(BRIDGE_AVAILABLE_STORAGE_KEY);
    }
  } catch {
    /* private mode */
  }
  const message: BridgeRelayAvailableMessage = {
    type: BRIDGE_RELAY_AVAILABLE,
    available,
  };
  // 广播给所有同源 iframe（不限于 Home secondary），避免漏标 role 的嵌页收不到就绪态
  document.querySelectorAll("iframe").forEach((frame) => {
    try {
      frame.contentWindow?.postMessage(message, window.location.origin);
    } catch {
      /* ignore cross-origin */
    }
  });
}

export function isTopBridgeMarkedAvailable(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem(BRIDGE_AVAILABLE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function newRequestId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `br-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getLocalBridge(): JavascriptBridge | null {
  if (typeof window === "undefined") return null;
  return (
    (window as unknown as { WebViewJavascriptBridge?: JavascriptBridge }).WebViewJavascriptBridge ??
    null
  );
}

async function callLocalBridge<T>(options: {
  action: string;
  params?: unknown;
  resultEvent: string | null;
  timeoutMs: number;
}): Promise<T> {
  // 硬守卫：任何嵌套信号下禁止本地 callHandler（否则 Native Rejected: not_main_frame）
  if (shouldRelayBridgeToTop()) {
    console.warn(LOG_PREFIX, "callLocalBridge blocked; forcing relay", {
      action: options.action,
      href: typeof window !== "undefined" ? window.location.href : "",
    });
    return relayBridgeCallToTop<T>(options);
  }

  const bridge = getLocalBridge();
  if (!bridge) {
    throw new Error("WebViewJavascriptBridge is not available");
  }

  if (!options.resultEvent) {
    bridge.callHandler(options.action, options.params ?? {});
    return undefined as T;
  }

  return await new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error("Bridge call timed out"));
    }, options.timeoutMs);

    bridge.registerHandler(options.resultEvent!, (payload) => {
      window.clearTimeout(timeoutId);
      resolve(payload as T);
    });

    try {
      bridge.callHandler(options.action, options.params ?? {});
    } catch (err) {
      window.clearTimeout(timeoutId);
      reject(err instanceof Error ? err : new Error("Bridge call failed"));
    }
  });
}

async function relayBridgeCallToTop<T>(options: {
  action: string;
  params?: unknown;
  resultEvent: string | null;
  timeoutMs: number;
}): Promise<T> {
  const requestId = newRequestId();
  const target = getBridgeRelayTargetWindow();
  const message: BridgeRelayRequestMessage = {
    type: BRIDGE_RELAY_REQUEST,
    requestId,
    action: options.action,
    params: options.params ?? {},
    resultEvent: options.resultEvent,
  };

  console.log(LOG_PREFIX, "relayBridgeCallToTop", {
    action: options.action,
    requestId,
    targetIsParent: target === window.parent,
    targetIsTop: (() => {
      try {
        return target === window.top;
      } catch {
        return false;
      }
    })(),
    href: window.location.href,
  });

  return await new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      window.removeEventListener("message", onMessage);
      reject(new Error("Bridge relay timed out"));
    }, options.timeoutMs);

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!isBridgeRelayTargetSource(event.source)) return;
      const data = event.data as BridgeRelayResultMessage | null;
      if (!data || data.type !== BRIDGE_RELAY_RESULT || data.requestId !== requestId) return;

      window.clearTimeout(timeoutId);
      window.removeEventListener("message", onMessage);
      if (data.error) {
        reject(new Error(data.error));
        return;
      }
      resolve(data.payload as T);
    };

    window.addEventListener("message", onMessage);
    try {
      target.postMessage(message, window.location.origin);
    } catch (err) {
      window.clearTimeout(timeoutId);
      window.removeEventListener("message", onMessage);
      reject(err instanceof Error ? err : new Error("Bridge relay postMessage failed"));
    }

    // setLanguage 等无 Result：顶层处理完会回一条 payload:null
    if (!options.resultEvent) {
      // 仍等待顶层 ACK，避免静默丢失
    }
  });
}

/**
 * 统一 Bridge 调用入口：顶层直调；Home 二级 iframe 转发到顶层。
 * 子 frame 绝不能直调 callHandler。
 */
export async function invokeBridgeCall<T = unknown>(options: {
  action: string;
  params?: unknown;
  resultEvent: string | null;
  timeoutMs?: number;
}): Promise<T> {
  const timeoutMs = options.timeoutMs ?? 60_000;
  if (shouldRelayBridgeToTop()) {
    return relayBridgeCallToTop<T>({ ...options, timeoutMs });
  }
  return callLocalBridge<T>({ ...options, timeoutMs });
}

/** 串行化顶层转发，避免多个 iframe 共用同一 resultEvent 时互相覆盖。 */
let topRelayQueue: Promise<void> = Promise.resolve();

function enqueueTopRelay(task: () => Promise<void>): void {
  topRelayQueue = topRelayQueue.then(task, task);
}

/** 顶层 Host：处理来自二级 iframe 的转发请求。 */
export function handleBridgeRelayRequest(
  event: MessageEvent,
  getBridge: () => JavascriptBridge | null,
): boolean {
  if (typeof window === "undefined" || !isBridgeRelayHostFrame()) return false;
  if (event.origin !== window.location.origin) return false;
  const data = event.data as BridgeRelayRequestMessage | null;
  if (!data || data.type !== BRIDGE_RELAY_REQUEST) return false;
  if (!event.source || typeof (event.source as Window).postMessage !== "function") return false;

  const source = event.source as Window;
  const reply = (result: Omit<BridgeRelayResultMessage, "type" | "requestId">) => {
    const message: BridgeRelayResultMessage = {
      type: BRIDGE_RELAY_RESULT,
      requestId: data.requestId,
      ...result,
    };
    source.postMessage(message, window.location.origin);
  };

  enqueueTopRelay(async () => {
    const bridge = getBridge();
    if (!bridge) {
      reply({ error: "WebViewJavascriptBridge is not available on top frame" });
      return;
    }

    console.log(LOG_PREFIX, "host callHandler", {
      action: data.action,
      requestId: data.requestId,
      href: window.location.href,
    });

    if (!data.resultEvent) {
      try {
        bridge.callHandler(data.action, data.params ?? {});
        reply({ payload: null });
      } catch (err) {
        reply({
          error: err instanceof Error ? err.message : "Bridge call failed",
        });
      }
      return;
    }

    await new Promise<void>((resolve) => {
      let settled = false;
      const settle = (result: Omit<BridgeRelayResultMessage, "type" | "requestId">) => {
        if (settled) return;
        settled = true;
        reply(result);
        resolve();
      };

      // 单次调用最长等待，防止队列永久卡住
      const timeoutId = window.setTimeout(() => {
        settle({ error: "Bridge call timed out on top frame" });
      }, 180_000);

      try {
        bridge.registerHandler(data.resultEvent!, (payload) => {
          window.clearTimeout(timeoutId);
          settle({ payload });
        });
        bridge.callHandler(data.action, data.params ?? {});
      } catch (err) {
        window.clearTimeout(timeoutId);
        settle({
          error: err instanceof Error ? err.message : "Bridge call failed",
        });
      }
    });
  });

  return true;
}
